// PTS Offset viewer: draws the sample model, the original .pts trim line and the shifted (X/Y/Z) one on a 2D canvas.
// No 3D library: orthographic projection, painter's algorithm, two-sided flat shading.
// Coordinates are the .pts/.stl ones (mm): X right and Y up in the top view, Z towards the viewer.

const DEG = Math.PI / 180;
export const VIEWS = {
    top: {yaw: 0, pitch: 0},
    front: {yaw: 0, pitch: 90 * DEG},
    '3d': {yaw: -25 * DEG, pitch: 55 * DEG},
};
const COLORS = {
    background: '#f7f9fb',
    mesh: [176, 190, 204],
    original: '#546e7a',
    shifted: '#d32f2f',
    arrow: '#d32f2f',
    text: '#37474f',
    axes: {X: '#e53935', Y: '#43a047', Z: '#1e88e5'},
};

export function createPtsOffsetView(canvas) {
    const ctx = canvas.getContext('2d');
    const state = {
        sample: null, positions: null, indices: null, pts: null, center: [0, 0, 0], radius: 1, anchor: 0,
        dx: 0, dy: 0, dz: 0, magnify: 1, yaw: VIEWS.top.yaw, pitch: VIEWS.top.pitch, zoom: 1, panX: 0, panY: 0,
        width: 0, height: 0, dpr: 1, frame: 0, drag: null,
    };

    const view = (x, y, z) => {
        // yaw around Z, then tilt from the top view (pitch 0) to the front view (pitch 90°)
        const qx = x - state.center[0], qy = y - state.center[1], qz = z - state.center[2];
        const cy = Math.cos(state.yaw), sy = Math.sin(state.yaw), cp = Math.cos(state.pitch), sp = Math.sin(state.pitch);
        const x1 = qx * cy - qy * sy, y1 = qx * sy + qy * cy;
        return [x1, y1 * cp + qz * sp, qz * cp - y1 * sp];
    };
    const scale = () => 0.92 * Math.min(state.width, state.height) / (2 * state.radius) * state.zoom;
    const toScreen = (v, s) => [state.width / 2 + state.panX + v[0] * s, state.height / 2 + state.panY - v[1] * s];

    const drawMesh = s => {
        const P = state.positions, I = state.indices;
        if (!P) return;
        const n = P.length / 3, sx = new Float32Array(n), sy = new Float32Array(n), vx = new Float32Array(n),
            vy = new Float32Array(n), vz = new Float32Array(n);
        for (let i = 0; i < n; i++) {
            const v = view(P[3 * i], P[3 * i + 1], P[3 * i + 2]);
            vx[i] = v[0]; vy[i] = v[1]; vz[i] = v[2];
            const p = toScreen(v, s);
            sx[i] = p[0]; sy[i] = p[1];
        }
        const faces = I.length / 3, order = new Uint32Array(faces), depth = new Float32Array(faces);
        for (let f = 0; f < faces; f++) {
            order[f] = f;
            depth[f] = vz[I[3 * f]] + vz[I[3 * f + 1]] + vz[I[3 * f + 2]];
        }
        order.sort((a, b) => depth[a] - depth[b]);
        ctx.lineWidth = 0.6;
        ctx.lineJoin = 'round';
        for (let k = 0; k < faces; k++) {
            const f = order[k], a = I[3 * f], b = I[3 * f + 1], c = I[3 * f + 2];
            const ux = vx[b] - vx[a], uy = vy[b] - vy[a], uz = vz[b] - vz[a];
            const wx = vx[c] - vx[a], wy = vy[c] - vy[a], wz = vz[c] - vz[a];
            const nx = uy * wz - uz * wy, ny = uz * wx - ux * wz, nz = ux * wy - uy * wx;
            const len = Math.hypot(nx, ny, nz) || 1;
            const light = 0.42 + 0.58 * Math.abs(nz) / len;
            const color = `rgb(${COLORS.mesh.map(ch => Math.round(ch * light)).join(',')})`;
            ctx.fillStyle = color;
            ctx.strokeStyle = color;
            ctx.beginPath();
            ctx.moveTo(sx[a], sy[a]);
            ctx.lineTo(sx[b], sy[b]);
            ctx.lineTo(sx[c], sy[c]);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
        }
    };

    const drawLine = (s, ox, oy, oz, color, width, dash) => {
        const Q = state.pts;
        if (!Q) return;
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.setLineDash(dash || []);
        ctx.beginPath();
        for (let i = 0; i < Q.length; i += 3) {
            const p = toScreen(view(Q[i] + ox, Q[i + 1] + oy, Q[i + 2] + oz), s);
            if (i === 0) ctx.moveTo(p[0], p[1]); else ctx.lineTo(p[0], p[1]);
        }
        ctx.stroke();
        ctx.setLineDash([]);
    };

    const arrow = (x0, y0, x1, y1, color, width) => {
        const len = Math.hypot(x1 - x0, y1 - y0);
        if (len < 1) return;
        const ux = (x1 - x0) / len, uy = (y1 - y0) / len, head = Math.min(9, len * 0.6);
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = width;
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1 - ux * head * 0.8, y1 - uy * head * 0.8);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x1 - ux * head - uy * head * 0.45, y1 - uy * head + ux * head * 0.45);
        ctx.lineTo(x1 - ux * head + uy * head * 0.45, y1 - uy * head - ux * head * 0.45);
        ctx.closePath();
        ctx.fill();
    };

    const drawShiftArrow = (s, ox, oy, oz) => {
        const Q = state.pts;
        if (!Q || (ox === 0 && oy === 0 && oz === 0)) return;
        const i = state.anchor;
        const a = toScreen(view(Q[i], Q[i + 1], Q[i + 2]), s);
        const b = toScreen(view(Q[i] + ox, Q[i + 1] + oy, Q[i + 2] + oz), s);
        ctx.fillStyle = COLORS.original;
        ctx.beginPath();
        ctx.arc(a[0], a[1], 3, 0, 2 * Math.PI);
        ctx.fill();
        arrow(a[0], a[1], b[0], b[1], COLORS.arrow, 2);
    };

    const drawAxes = () => {
        const ox = 34, oy = state.height - 30, size = 22;
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        for (const [name, d] of [['X', [1, 0, 0]], ['Y', [0, 1, 0]], ['Z', [0, 0, 1]]]) {
            const v = view(state.center[0] + d[0], state.center[1] + d[1], state.center[2] + d[2]);
            const ex = ox + v[0] * size, ey = oy - v[1] * size;
            if (Math.hypot(ex - ox, ey - oy) < 3) {
                // axis along the line of sight: a dot when it points at the viewer, a cross when it points away
                ctx.fillStyle = COLORS.axes[name];
                ctx.strokeStyle = COLORS.axes[name];
                ctx.lineWidth = 2;
                ctx.beginPath();
                if (v[2] > 0) {
                    ctx.arc(ox, oy, 3.5, 0, 2 * Math.PI);
                    ctx.fill();
                } else {
                    ctx.moveTo(ox - 4, oy - 4); ctx.lineTo(ox + 4, oy + 4);
                    ctx.moveTo(ox + 4, oy - 4); ctx.lineTo(ox - 4, oy + 4);
                    ctx.stroke();
                }
                ctx.fillText(name, ox + 10, oy - 10);
                continue;
            }
            arrow(ox, oy, ex, ey, COLORS.axes[name], 2);
            ctx.fillStyle = COLORS.axes[name];
            ctx.fillText(name, ox + v[0] * (size + 9), oy - v[1] * (size + 9));
        }
    };

    const drawScaleBar = s => {
        const mm = [1, 2, 5, 10, 20, 50].find(v => v * s >= 50) || 50;
        const px = mm * s, x1 = state.width - 16, x0 = x1 - px, y = state.height - 18;
        ctx.strokeStyle = COLORS.text;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x0, y - 4); ctx.lineTo(x0, y); ctx.lineTo(x1, y); ctx.lineTo(x1, y - 4);
        ctx.stroke();
        ctx.fillStyle = COLORS.text;
        ctx.font = '11px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(`${mm} mm`, (x0 + x1) / 2, y - 5);
    };

    const draw = () => {
        state.frame = 0;
        const {width, height} = state;
        if (!width || !height) return;
        ctx.setTransform(state.dpr, 0, 0, state.dpr, 0, 0);   // same ratio the buffer was sized with
        ctx.fillStyle = COLORS.background;
        ctx.fillRect(0, 0, width, height);
        if (!state.positions) return;
        const s = scale(), m = state.magnify, ox = state.dx * m, oy = state.dy * m, oz = state.dz * m;
        drawMesh(s);
        drawLine(s, 0, 0, 0, COLORS.original, 1.6, [5, 4]);
        drawLine(s, ox, oy, oz, COLORS.shifted, 2);
        drawShiftArrow(s, ox, oy, oz);
        drawAxes();
        drawScaleBar(s);
    };
    const redraw = () => {
        if (!state.frame) state.frame = window.requestAnimationFrame(draw);
    };

    // moving the window to a monitor with another scaling changes devicePixelRatio but not the CSS size
    let dprQuery = null;
    const watchDpr = () => {
        if (dprQuery) dprQuery.removeEventListener('change', resize);
        dprQuery = window.matchMedia ? window.matchMedia(`(resolution: ${state.dpr}dppx)`) : null;
        if (dprQuery) dprQuery.addEventListener('change', resize);
    };
    function resize() {
        state.dpr = window.devicePixelRatio || 1;
        state.width = canvas.clientWidth;
        state.height = canvas.clientHeight;
        canvas.width = Math.max(1, Math.round(state.width * state.dpr));
        canvas.height = Math.max(1, Math.round(state.height * state.dpr));
        watchDpr();
        redraw();
    }

    const onPointerDown = e => {
        if (state.drag) return;                       // one pointer at a time (a second finger is ignored)
        canvas.setPointerCapture(e.pointerId);
        state.drag = {id: e.pointerId, x: e.clientX, y: e.clientY, pan: e.button !== 0 || e.shiftKey};
    };
    const onPointerMove = e => {
        if (!state.drag || e.pointerId !== state.drag.id) return;
        const ddx = e.clientX - state.drag.x, ddy = e.clientY - state.drag.y;
        state.drag.x = e.clientX;
        state.drag.y = e.clientY;
        if (state.drag.pan) {
            state.panX += ddx;
            state.panY += ddy;
        } else {
            state.yaw += ddx * 0.01;
            state.pitch = Math.min(Math.PI, Math.max(0, state.pitch + ddy * 0.01));
        }
        redraw();
    };
    const onPointerUp = e => {
        if (!state.drag || e.pointerId !== state.drag.id) return;
        state.drag = null;
        if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    };
    const onWheel = e => {
        e.preventDefault();
        // zoom around the point under the cursor, so what you are looking at stays in view
        const zoom = Math.min(40, Math.max(0.5, state.zoom * Math.exp(-e.deltaY * 0.0015)));
        // clientX - rect (not offsetX): stays right with browser zoom
        const rect = canvas.getBoundingClientRect();
        const cx = (e.clientX - rect.left) * state.width / (rect.width || 1) - state.width / 2;
        const cy = (e.clientY - rect.top) * state.height / (rect.height || 1) - state.height / 2;
        const k = zoom / state.zoom;
        state.panX = cx - (cx - state.panX) * k;
        state.panY = cy - (cy - state.panY) * k;
        state.zoom = zoom;
        redraw();
    };
    const onContextMenu = e => e.preventDefault();

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);
    canvas.addEventListener('wheel', onWheel, {passive: false});
    canvas.addEventListener('contextmenu', onContextMenu);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    if (observer) observer.observe(canvas);
    resize();

    return {
        setSample(sample) {
            const P = sample.mesh.positions, Q = sample.pts;
            let min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
            for (let i = 0; i < P.length; i += 3) {
                for (let k = 0; k < 3; k++) {
                    min[k] = Math.min(min[k], P[i + k]);
                    max[k] = Math.max(max[k], P[i + k]);
                }
            }
            state.positions = P;
            state.indices = sample.mesh.indices;
            state.pts = Q;
            state.center = min.map((v, k) => (v + max[k]) / 2);
            state.radius = Math.max(1, Math.hypot(max[0] - min[0], max[1] - min[1], max[2] - min[2]) / 2);
            // the arrow starts at the trim-line point with the largest Y (front of the arch in the top view)
            let best = 0;
            for (let i = 0; i < Q.length; i += 3) if (Q[i + 1] > Q[best + 1]) best = i;
            state.anchor = best;
            redraw();
        },
        setOffset(dx, dy, dz, magnify) {
            state.dx = Number.isFinite(dx) ? dx : 0;
            state.dy = Number.isFinite(dy) ? dy : 0;
            state.dz = Number.isFinite(dz) ? dz : 0;
            state.magnify = magnify > 0 ? magnify : 1;
            redraw();
        },
        setView(name) {
            const v = VIEWS[name] || VIEWS.top;
            state.yaw = v.yaw;
            state.pitch = v.pitch;
            state.zoom = 1;
            state.panX = 0;
            state.panY = 0;
            redraw();
        },
        // zoom in on the shift arrow (a 1 mm shift is ~2% of the arch: hard to see at full view), never so much
        // that the arrow (longer with Exaggerate on) leaves the canvas
        focusShift(factor) {
            const Q = state.pts;
            if (!Q || !state.width) return;
            const i = state.anchor, m = state.magnify, side = Math.min(state.width, state.height);
            const shown = Math.hypot(state.dx, state.dy, state.dz) * m * 0.92 * side / (2 * state.radius);
            state.zoom = Math.min(40, Math.max(0.5, Math.min(factor, 0.6 * side / Math.max(shown, 1e-6))));
            const v = view(Q[i] + state.dx * m / 2, Q[i + 1] + state.dy * m / 2, Q[i + 2] + state.dz * m / 2);
            const s = scale();
            state.panX = -v[0] * s;
            state.panY = v[1] * s;
            redraw();
        },
        destroy() {
            if (state.frame) window.cancelAnimationFrame(state.frame);
            if (observer) observer.disconnect();
            if (dprQuery) dprQuery.removeEventListener('change', resize);
            canvas.removeEventListener('pointerdown', onPointerDown);
            canvas.removeEventListener('pointermove', onPointerMove);
            canvas.removeEventListener('pointerup', onPointerUp);
            canvas.removeEventListener('pointercancel', onPointerUp);
            canvas.removeEventListener('wheel', onWheel);
            canvas.removeEventListener('contextmenu', onContextMenu);
        },
        _project: (x, y, z) => view(x, y, z),
        _state: state,
    };
}
